import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { CardContent, Typography, useTheme } from '@mui/material';
import { amber } from '@mui/material/colors';
import { orange } from '@mui/material/colors';
import { useCallback, useEffect, useState } from 'react';
import ManagementApi from '../../../apis/ManagementApi';
import useApi from '../../../lib/useApi';
import LoadingCircle from '../../LoadingCircle/LoadingCircle';
import ManagementGridProps from '../lib/ManagementGridProps';
import IconRow from './IconRow';

/**
 * ImportantDates component displays crucial dates related to subscriptions and licensing.
 */
const ImportantDates = (props: ManagementGridProps) => {
  // State variables for the lapse date, loading, and error states
  const [yearlyLapseDate, setYearlyLapseDate] = useState<string | null>(null);
  const [renewalDate, setRenewalDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const theme = useTheme();
  const [renewalSubtitle, setRenewalSubtitle] = useState(
    'All licenses will be automatically renewed on this date.'
  );
  const [renewalColor, setRenewalColor] = useState(theme.palette.primary.main);
  const [renewalIcon, setRenewalIcon] = useState(
    <EventRepeatRoundedIcon sx={{ height: '28px', width: '28px' }} />
  );

  // Initialize the ManagementApi using the custom useApi hook
  const managementApi = useApi(ManagementApi);

  /**
   * Formats a Date object into a readable string format.
   *
   * @param {Date} date - The date to format.
   * @returns {string} The formatted date string.
   */
  const formatDate = useCallback((date: Date) => {
    // Format the date as "Month Day, Year" (e.g., June 16, 2024)
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, []);

  /**
   * Fetches important dates and subscription status from the API.
   */
  useEffect(() => {
    setIsLoading(true);
    setError(false);
    const fetchDates = async () => {
      try {
        // Fetch the dates from the API
        const lapseDate = props.orgId
          ? await managementApi.GetYearlyLapseDateWithOrgId(props.orgId)
          : await managementApi.GetYearlyLapseDate();
        const subStatus = props.orgId
          ? await managementApi.GetSubscriptionStatusWithOrgId(props.orgId)
          : await managementApi.GetSubscriptionStatus();

        // Set the states
        if (!subStatus.active) {
          setRenewalSubtitle(
            'Your licenses have expired. Please renew to maintain access.'
          );
          setRenewalColor(theme.palette.error.main);
          setRenewalIcon(
            <ErrorOutlineRoundedIcon sx={{ height: '28px', width: '28px' }} />
          );
        } else if (!subStatus.willRenew) {
          setRenewalSubtitle(
            'Your licenses expire on this date and aren’t set to renew automatically. Please renew to keep access.'
          );
          setRenewalColor(orange.A400);
          setRenewalIcon(
            <WarningAmberRoundedIcon sx={{ height: '28px', width: '28px' }} />
          );
        }

        // Format and set the dates
        setYearlyLapseDate(formatDate(lapseDate));
        setRenewalDate(formatDate(subStatus.endOfPeriod));
      } catch (err) {
        console.error('Error fetching yearly lapse date:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDates();
  }, [formatDate, managementApi, props.orgId, theme.palette.error.main]);

  return (
    <CardContent sx={{ position: 'relative', height: '100%', width: '100%' }}>
      {/* Title Section */}
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 500,
          textTransform: 'uppercase',
          lineHeight: 2.66,
        }}
      >
        Important Dates
      </Typography>
      {error ? (
        <h1>Error</h1>
      ) : (
        <>
          <IconRow
            baseId="renewal-date"
            content={renewalDate ?? 'Loading...'}
            icon={renewalIcon}
            subtitle={renewalSubtitle}
            color={renewalColor}
          />
          <IconRow
            baseId="yearly-lapse"
            content={yearlyLapseDate ?? 'Loading...'}
            icon={
              <AutorenewRoundedIcon sx={{ height: '28px', width: '28px' }} />
            }
            subtitle="On this date, all member training progress will reset to 0%."
            color={amber.A400}
          />
        </>
      )}
      <LoadingCircle isLoading={isLoading} hideText />
    </CardContent>
  );
};

export default ImportantDates;
